import React, { useEffect, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { useEffectFn } from '@ngneat/effects-hooks';
import { useNavigate } from 'react-router-dom';
import { useObservable } from '@ngneat/react-rxjs';
import { useTranslation } from 'react-i18next';
import { emitOnce } from '@ngneat/elf';
import { useSnackbar } from 'notistack';
import { finalize, Observable } from 'rxjs';
import { GridValueGetterParams } from '@mui/x-data-grid-pro';

import {
  searchSuppliersUnitsEffect,
  SuppliersUnit,
  suppliersUnitsQuery,
  suppliersUnitsService
} from '@store/suppliers/units';
import { GlobalStatus } from '@store/common/globalStatus.enum';

import SearchComponent from '@components/inputs/search/Search.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import NewTableComponent from '@components/table/NewTable.component';
import ConfirmModal from '@components/modal/Confirm.modal';

import { ConfirmActionProps, OtherActionType } from '@utils/table/columnTypes/actions.columnType';
import COLUMN_TYPES from '@utils/table/columnTypes.table';

const SuppliersUnitsTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const searchSuppliers = useEffectFn(searchSuppliersUnitsEffect);

  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);

  const [{ suppliersUnits, loading }] = useObservable(suppliersUnitsQuery.suppliersUnits$);
  const [selectedSuppliersUnits] = useObservable(suppliersUnitsQuery.selectedSuppliersUnits$);

  const [filterStatus] = useObservable(suppliersUnitsQuery.status$);
  const [filters] = useObservable(suppliersUnitsQuery.filters$);
  const [sortModel] = useObservable(suppliersUnitsQuery.sort$);
  const [tablePagination] = useObservable(suppliersUnitsQuery.pagination$);

  useEffect(() => {
    searchSuppliers({ status: filterStatus, filters, tablePagination, sort: sortModel });
  }, [filters, filterStatus, searchSuppliers, sortModel, tablePagination]);

  const handleConfirmValidation = (type: OtherActionType, supplierUnit: SuppliersUnit) => (handleStopLoading: () => void) => {
    let validationObservable: Observable<any> | undefined = undefined;
    switch (type) {
      case OtherActionType.DELETE:
        validationObservable = suppliersUnitsService.deleteSuppliersUnitInList(supplierUnit.id);
        break;
      case OtherActionType.ARCHIVE:
        validationObservable = suppliersUnitsService.updateSuppliersUnitsStatusInList(supplierUnit.id, GlobalStatus.ARCHIVED);
        break;
    }
    validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
      next: () => {
        enqueueSnackbar(t(`${OtherActionType.translationKey(type)}.success.supplierUnit`), { variant: 'success' });
        setOpenConfirmAction(false);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <Stack flex={1} spacing={2} pb="25px">
      <Stack direction="row" spacing={2} alignItems="lex-start" justifyContent="space-between">
        <Box width="500px">
          <SearchComponent
            value={filters.search}
            handleChange={(value) => {
              emitOnce(() => {
                suppliersUnitsService.setFilters('search')(value);
                suppliersUnitsService.setPagination('page')(1);
              });
            }} />
        </Box>
        <TableFiltersComponent
          filters={filters}
          handleSave={suppliersUnitsService.setFilters('tableFilters')}
          handleReset={() => suppliersUnitsService.resetFilters()} />
      </Stack>
      <NewTableComponent
        loading={loading}
        columns={[
          COLUMN_TYPES.TEXT_BOLD({
            field: 'organisationUnit.id',
            label: t('suppliers.supplierUnitsTable.id'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersUnit>) => params.row.id,
            width: 100
          }),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'organisationUnit.name',
            label: t('suppliers.supplierUnitsTable.name'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersUnit>) => params.row.name,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({
            field: 'organisation.name',
            label: t('suppliers.supplierUnitsTable.supplier'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersUnit>) => params.row.organisationName,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({ field: 'phone', label: t('suppliers.supplierUnitsTable.phone'), width: 130 }),
          COLUMN_TYPES.TEXT({
            field: 'email',
            color: 'secondary',
            label: t('suppliers.supplierUnitsTable.email'),
            fullWidth: true,
            minWidth: 300
          }),
          COLUMN_TYPES.TEXT({
            field: 'addressOrgUnit.city',
            color: 'secondary',
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersUnit>) => params.row.city,
            label: t('suppliers.supplierUnitsTable.city'),
            width: 250
          }),
          COLUMN_TYPES.TEXT({
            field: 'organisationUnit.region',
            color: 'secondary',
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersUnit>) => params.row.region,
            label: t('suppliers.supplierUnitsTable.region'),
            width: 150
          }),
          COLUMN_TYPES.TEXT({
            field: 'addressOrgUnit.country',
            color: 'secondary',
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersUnit>) => params.row.country,
            label: t('suppliers.supplierUnitsTable.country'),
            width: 150
          }),
          COLUMN_TYPES.ACTIONS({
            handleShowRow: (suppliersUnit: SuppliersUnit) => navigate(`${suppliersUnit.id}`),
            otherActions: [
              {
                type: OtherActionType.ARCHIVE,
                handleClick: (suppliersUnit: SuppliersUnit) => setOpenConfirmAction({
                  type: OtherActionType.ARCHIVE,
                  row: suppliersUnit
                }),
                hidden: (suppliersUnit: SuppliersUnit) => suppliersUnit.status === GlobalStatus.ARCHIVED || filterStatus === GlobalStatus.ARCHIVED,
              },
              {
                type: OtherActionType.DELETE,
                handleClick: (suppliersUnit: SuppliersUnit) => setOpenConfirmAction({
                  type: OtherActionType.DELETE,
                  row: suppliersUnit
                }),
              },
            ]
          }),
        ]}
        handlePageSizeChange={suppliersUnitsService.setPagination('limit')}
        pageSize={tablePagination.limit}
        handlePageChange={(page) => suppliersUnitsService.setPagination('page')(page + 1)}
        page={tablePagination.page - 1}
        handleSortModelChange={suppliersUnitsService.setSort}
        sortModel={[sortModel]}
        handleRowSelected={suppliersUnitsService.setSelectedSuppliersUnits}
        rowsSelected={selectedSuppliersUnits}
        handleReload={() => searchSuppliers({ status: filterStatus, filters, tablePagination, sort: sortModel })}
        totalRows={suppliersUnitsQuery.totalRows}
        data={suppliersUnits} />
      {!!openConfirmAction && (
        <ConfirmModal
          handleClose={() => setOpenConfirmAction(false)}
          description={t(`${OtherActionType.translationKey(openConfirmAction.type)}.description.supplierUnit`)}
          handleValidate={handleConfirmValidation(openConfirmAction.type, openConfirmAction.row)}
          title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
          validateButton={OtherActionType.label(openConfirmAction.type)} />
      )}
    </Stack>
  );
};

export default SuppliersUnitsTable;
